import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import StatsCard from '../components/StatsCard';
import {
  Send,
  Users,
  CheckCircle,
  AlertTriangle,
  FileSpreadsheet,
  MailWarning,
  Eye,
  MousePointerClick,
  TrendingUp,
  Activity,
  UserMinus,
  Calendar,
  Zap
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const HEALTH_COLORS: Record<string, string> = {
  SUBSCRIBED: '#10b981',
  BOUNCED: '#f43f5e',
  INVALID: '#94a3b8',
  UNSUBSCRIBED: '#f59e0b',
  COMPLAINED: '#f97316'
};

const BADGE_COLOR: Record<string, string> = {
  DRAFT: 'bg-slate-100 text-slate-600 border-slate-200',
  SCHEDULED: 'bg-primary-500/10 text-primary-600 border-primary-500/20',
  PROCESSING: 'bg-primary-500/10 text-primary-600 border-primary-500/20',
  SENT: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
  FAILED: 'bg-rose-500/10 text-rose-600 border-rose-500/20',
  PAUSED: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
  PAUSED_BOUNCE_LIMIT: 'bg-rose-500/10 text-rose-600 border-rose-500/20'
};

export const Dashboard: React.FC = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboardStats'],
    queryFn: async () => {
      const res = await apiClient.get('/analytics/dashboard');
      return res.data;
    },
    refetchInterval: (d: any) => {
      const active = d?.activeCampaigns || [];
      return active.length > 0 ? 10_000 : 30_000;
    }
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-[60vh]">
        <div className="w-10 h-10 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-accent-rose/10 border border-accent-rose/20 rounded-xl text-accent-rose">
        <h4 className="font-bold mb-2">Error Loading Dashboard Metrics</h4>
        <p className="text-sm">Please verify the backend server connection and try again.</p>
      </div>
    );
  }

  const counters = data?.counters || {};
  const chartData = data?.chartData || [];
  const recentCampaigns = data?.recentCampaigns || [];
  const activeCampaigns = data?.activeCampaigns || [];
  const recipientHealth = data?.recipientHealth || {};
  const sesQuota = data?.sesQuota || null;
  const nextScheduled = data?.nextScheduled || [];

  const healthDonutData = Object.entries(recipientHealth)
    .map(([status, count]) => ({ name: status, value: count as number }))
    .sort((a, b) => b.value - a.value);

  const totalRecipientHealth = healthDonutData.reduce((s, d) => s + d.value, 0);

  const sesUsedPct = sesQuota && sesQuota.max24Hour > 0
    ? (sesQuota.sentLast24Hours / sesQuota.max24Hour) * 100
    : 0;
  const sesBarColor = sesUsedPct >= 90 ? '#f43f5e' : sesUsedPct >= 70 ? '#f59e0b' : '#10b981';

  const tooltipStyle = {
    backgroundColor: '#ffffff',
    borderColor: 'rgba(0,0,0,0.1)',
    borderRadius: '8px',
    color: '#0f172a'
  };

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight uppercase">Campaign Dashboard</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">Monitor sending reputation, bulk pipelines, and email telemetry.</p>
        </div>
        <div className="glass-panel rounded-xl px-4 py-2.5 flex items-center gap-3">
          <div className="w-3 h-3 bg-accent-emerald rounded-full animate-pulse-slow"></div>
          <div>
            <div className="text-xs font-semibold text-slate-500 dark:text-slate-400">Warmup Score</div>
            <div className="text-sm font-bold text-slate-900 dark:text-white">
              {chartData[chartData.length - 1]?.reputation || 100}%
            </div>
          </div>
        </div>
      </div>

      {/* Stat Cards Row 1 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard title="Total Campaigns" value={counters.totalCampaigns || 0} icon={Send} color="blue" />
        <StatsCard title="Total Recipients" value={counters.totalRecipients || 0} icon={Users} color="violet" />
        <StatsCard title="Emails Sent" value={counters.sent || 0} icon={CheckCircle} color="emerald" />
        <StatsCard
          title="Delivery Rate"
          value={`${counters.rates?.deliveryRate || 0}%`}
          description={`${counters.delivered || 0} emails delivered`}
          icon={Activity}
          color="emerald"
        />
      </div>

      {/* Stat Cards Row 2 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
        <StatsCard title="Open Rate" value={`${counters.rates?.openRate || 0}%`} description={`${counters.opened || 0} unique opens`} icon={Eye} color="blue" />
        <StatsCard title="Click Rate" value={`${counters.rates?.clickRate || 0}%`} description={`${counters.clicked || 0} link clicks`} icon={MousePointerClick} color="violet" />
        <StatsCard title="Bounce Rate" value={`${counters.rates?.bounceRate || 0}%`} description={`${counters.bounced || 0} bounces`} icon={AlertTriangle} color="rose" />
        <StatsCard title="Complaint Rate" value={`${counters.rates?.complaintRate || 0}%`} description={`${counters.complained || 0} complaints`} icon={MailWarning} color="rose" />
        <StatsCard title="Unsubscribe Rate" value={`${counters.rates?.unsubscribeRate || 0}%`} description={`${counters.unsubscribed || 0} unsubscribes`} icon={UserMinus} color="rose" />
      </div>

      {/* Active Campaigns Panel — only shown when campaigns are live */}
      {activeCampaigns.length > 0 && (
        <div className="glass-panel rounded-2xl p-6">
          <h3 className="text-sm font-bold text-slate-800 dark:text-white uppercase tracking-wider flex items-center gap-2 mb-5">
            <div className="w-2 h-2 rounded-full bg-primary-500 animate-pulse"></div>
            Active Campaigns
            <span className="ml-1 px-2 py-0.5 rounded-full bg-primary-500/10 text-primary-600 dark:text-primary-500 text-xs font-semibold border border-primary-500/20">
              {activeCampaigns.length} live
            </span>
          </h3>
          <div className="space-y-4">
            {activeCampaigns.map((camp: any) => {
              const bounceColor = camp.stats.bounceRate >= camp.bounceRateThreshold * 100
                ? 'text-rose-600 dark:text-rose-400 font-bold'
                : camp.stats.bounceRate >= camp.bounceRateThreshold * 100 * 0.7
                ? 'text-amber-600 dark:text-amber-400'
                : 'text-slate-500 dark:text-slate-400';
              const sentPct = camp.stats.total > 0 ? Math.round((camp.stats.sent / camp.stats.total) * 100) : 0;

              return (
                <div key={camp.id} className="p-4 rounded-xl border border-slate-200 dark:border-white/5 bg-slate-50/50 dark:bg-white/2">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${BADGE_COLOR[camp.status] || ''}`}>
                        {camp.status === 'PAUSED_BOUNCE_LIMIT' ? 'BOUNCE LIMIT' : camp.status}
                      </span>
                      <span className="font-semibold text-sm text-slate-800 dark:text-white">{camp.name}</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs">
                      <span className="text-slate-500 dark:text-slate-400">
                        <strong className="text-slate-700 dark:text-slate-200">{camp.stats.sent.toLocaleString()}</strong> / {camp.stats.total.toLocaleString()} sent
                      </span>
                      <span className={bounceColor}>
                        {camp.stats.bounceRate}% bounce
                        {camp.status === 'PAUSED_BOUNCE_LIMIT' && ' — limit hit'}
                      </span>
                    </div>
                  </div>

                  {/* Chunk progress */}
                  {camp.chunksTotal > 0 && (
                    <div className="space-y-1.5">
                      <div className="flex gap-1">
                        {Array.from({ length: camp.chunksTotal }, (_: any, i: number) => (
                          <div
                            key={i}
                            className={`h-2 flex-1 rounded-sm transition-colors ${
                              i < camp.chunksSent
                                ? 'bg-emerald-500'
                                : camp.status === 'PAUSED_BOUNCE_LIMIT' && i === camp.chunksSent
                                ? 'bg-rose-500'
                                : 'bg-slate-200 dark:bg-slate-700'
                            }`}
                          />
                        ))}
                      </div>
                      <div className="flex items-center justify-between text-[10px] text-slate-500 dark:text-slate-400">
                        <span>{camp.chunksSent}/{camp.chunksTotal} chunks dispatched · 5 min intervals</span>
                        <span>{sentPct}% of list sent</span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Chart + Reputation */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 glass-panel rounded-2xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-sm font-bold text-slate-800 dark:text-white uppercase tracking-wider flex items-center gap-2">
              <TrendingUp size={16} className="text-primary-500" />
              Volume & Engagement (Last 30 Days)
            </h3>
          </div>
          <div className="h-80 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorSent" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#DC5829" stopOpacity={0.35}/>
                    <stop offset="95%" stopColor="#DC5829" stopOpacity={0.03}/>
                  </linearGradient>
                  <linearGradient id="colorOpened" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.35}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0.03}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={'rgba(0,0,0,0.06)'} />
                <XAxis
                  dataKey="date"
                  stroke="#64748b"
                  fontSize={11}
                  tickLine={false}
                  tickFormatter={(d: string) => {
                    const dt = new Date(d);
                    return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                  }}
                />
                <YAxis
                  stroke="#64748b"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v: number) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v)}
                />
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(value: number, name: string) => [value.toLocaleString(), name]}
                  labelFormatter={(label: string) => new Date(label).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                />
                <Legend verticalAlign="top" height={36} iconType="circle" />
                <Area name="Sent" type="monotone" dataKey="sent" stroke="#DC5829" strokeWidth={2} fillOpacity={1} fill="url(#colorSent)" />
                <Area name="Opens" type="monotone" dataKey="opens" stroke="#10b981" strokeWidth={2} fillOpacity={1} fill="url(#colorOpened)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Reputation card */}
        {(() => {
          const todayScore = chartData[chartData.length - 1]?.reputation ?? 100;
          const yesterdayScore = chartData[chartData.length - 2]?.reputation ?? todayScore;
          const delta = todayScore - yesterdayScore;
          const recentDays = chartData.slice(-7);
          const scoreColor = todayScore >= 80 ? '#10b981' : todayScore >= 60 ? '#f59e0b' : '#f43f5e';
          const circumference = 2 * Math.PI * 54;

          return (
            <div className="glass-panel rounded-2xl p-6 flex flex-col gap-4">
              <h3 className="text-sm font-bold text-slate-800 dark:text-white uppercase tracking-wider flex items-center gap-2">
                <TrendingUp size={16} className="text-accent-emerald" />
                Reputation & Warmup
              </h3>

              {/* Score + delta */}
              <div className="flex items-center gap-5">
                <div className="relative w-28 h-28 shrink-0 flex items-center justify-center">
                  <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
                    <circle cx="60" cy="60" r="54" stroke={'#e2e8f0'} strokeWidth="10" fill="transparent" />
                    <circle
                      cx="60" cy="60" r="54"
                      stroke={scoreColor}
                      strokeWidth="10" fill="transparent"
                      strokeDasharray={circumference}
                      strokeDashoffset={circumference - (circumference * todayScore) / 100}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute text-center">
                    <div className="text-2xl font-extrabold text-slate-800 dark:text-white" style={{ color: scoreColor }}>{todayScore}</div>
                    <div className="text-[9px] text-slate-400 uppercase tracking-wider font-semibold">Score</div>
                  </div>
                </div>

                <div className="flex flex-col gap-2 flex-1">
                  <div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">vs yesterday</div>
                    <div className={`text-sm font-bold ${delta > 0 ? 'text-emerald-500' : delta < 0 ? 'text-rose-500' : 'text-slate-500'}`}>
                      {delta > 0 ? `+${delta}` : delta === 0 ? '—' : delta} pts
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">Bounce threshold</div>
                    <div className="text-sm font-bold text-rose-500">&lt;2.0%</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">Status</div>
                    <div className="text-sm font-bold" style={{ color: scoreColor }}>
                      {todayScore >= 80 ? 'Healthy' : todayScore >= 60 ? 'Degraded' : 'At Risk'}
                    </div>
                  </div>
                </div>
              </div>

              {/* 7-day sparkline */}
              {recentDays.length > 1 && (
                <div className="border-t border-slate-200 dark:border-white/5 pt-3">
                  <div className="text-[10px] text-slate-400 mb-1.5 uppercase tracking-wider">7-day trend</div>
                  <div className="h-10 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={recentDays} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
                        <defs>
                          <linearGradient id="repGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={scoreColor} stopOpacity={0.3} />
                            <stop offset="100%" stopColor={scoreColor} stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <Area type="monotone" dataKey="reputation" stroke={scoreColor} strokeWidth={1.5} fill="url(#repGrad)" dot={false} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex justify-between text-[9px] text-slate-400 mt-0.5">
                    <span>{recentDays[0]?.date ? new Date(recentDays[0].date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''}</span>
                    <span>Today</span>
                  </div>
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* Recipient Health + SES Quota + Next Scheduled */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Recipient Health Donut */}
        <div className="glass-panel rounded-2xl p-6">
          <h3 className="text-sm font-bold text-slate-800 dark:text-white uppercase tracking-wider flex items-center gap-2 mb-4">
            <Users size={16} className="text-primary-500" />
            Recipient Health
          </h3>
          {totalRecipientHealth > 0 ? (
            <>
              <div className="flex justify-center">
                <PieChart width={180} height={180}>
                  <Pie
                    data={healthDonutData}
                    cx={85}
                    cy={85}
                    innerRadius={52}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {healthDonutData.map((entry) => (
                      <Cell key={entry.name} fill={HEALTH_COLORS[entry.name] || '#94a3b8'} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={tooltipStyle}
                    formatter={(value: number, name: string) => [
                      `${value.toLocaleString()} (${((value / totalRecipientHealth) * 100).toFixed(1)}%)`,
                      name
                    ]}
                  />
                </PieChart>
              </div>
              <div className="space-y-2 mt-2">
                {healthDonutData.map((entry) => (
                  <div key={entry.name} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: HEALTH_COLORS[entry.name] || '#94a3b8' }} />
                      <span className="text-slate-600 dark:text-slate-300">{entry.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-700 dark:text-slate-200">{entry.value.toLocaleString()}</span>
                      <span className="text-slate-400 w-10 text-right">{((entry.value / totalRecipientHealth) * 100).toFixed(1)}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-slate-400 text-sm">No recipient data yet.</div>
          )}
        </div>

        {/* SES Daily Quota */}
        <div className="glass-panel rounded-2xl p-6 flex flex-col">
          <h3 className="text-sm font-bold text-slate-800 dark:text-white uppercase tracking-wider flex items-center gap-2 mb-4">
            <Zap size={16} className="text-amber-500" />
            SES Daily Quota
          </h3>
          {sesQuota ? (
            <div className="flex flex-col flex-1 justify-between">
              <div className="space-y-5">
                <div className="text-center">
                  <div className="text-3xl font-extrabold text-slate-800 dark:text-white">
                    {sesQuota.sentLast24Hours.toLocaleString()}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    of {sesQuota.max24Hour.toLocaleString()} daily limit used
                  </div>
                </div>

                {/* Bar */}
                <div className="space-y-1.5">
                  <div className="w-full h-3 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(sesUsedPct, 100)}%`, backgroundColor: sesBarColor }}
                    />
                  </div>
                  <div className="flex justify-between text-[10px] text-slate-500 dark:text-slate-400">
                    <span>{sesUsedPct.toFixed(1)}% used</span>
                    <span>{(sesQuota.max24Hour - sesQuota.sentLast24Hours).toLocaleString()} remaining</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-xl bg-slate-100 dark:bg-white/5 text-center">
                    <div className="text-sm font-bold text-slate-700 dark:text-slate-200">{sesQuota.maxSendRate}/s</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">Max send rate</div>
                  </div>
                  <div className="p-3 rounded-xl text-center" style={{ backgroundColor: `${sesBarColor}15` }}>
                    <div className="text-sm font-bold" style={{ color: sesBarColor }}>
                      {sesUsedPct >= 90 ? 'Critical' : sesUsedPct >= 70 ? 'Warning' : 'Healthy'}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5">Quota status</div>
                  </div>
                </div>
              </div>

              <div className="border-t border-slate-200 dark:border-white/5 pt-4 mt-4 text-center">
                <span className="text-xs text-slate-500 dark:text-slate-400">Resets every 24 hours</span>
              </div>
            </div>
          ) : (
            <div className="flex flex-col flex-1 items-center justify-center text-center space-y-2 py-4">
              <div className="text-slate-400 text-sm">SES quota unavailable</div>
              <div className="text-xs text-slate-400">Mock mode or insufficient permissions</div>
            </div>
          )}
        </div>

        {/* Next Scheduled */}
        <div className="glass-panel rounded-2xl p-6 flex flex-col">
          <h3 className="text-sm font-bold text-slate-800 dark:text-white uppercase tracking-wider flex items-center gap-2 mb-4">
            <Calendar size={16} className="text-primary-500" />
            Next Scheduled
          </h3>
          {nextScheduled.length > 0 ? (
            <div className="space-y-3">
              {nextScheduled.map((camp: any, i: number) => {
                const dt = new Date(camp.scheduledAt);
                const isToday = dt.toDateString() === new Date().toDateString();
                const isTomorrow = dt.toDateString() === new Date(Date.now() + 86400000).toDateString();
                const dayLabel = isToday ? 'Today' : isTomorrow ? 'Tomorrow' : dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                return (
                  <div key={camp.id} className={`flex items-start gap-3 p-3 rounded-xl ${i === 0 ? 'bg-primary-500/5 border border-primary-500/15' : 'bg-slate-50/50 dark:bg-white/2 border border-slate-200 dark:border-white/5'}`}>
                    <div className={`w-8 h-8 rounded-lg flex flex-col items-center justify-center shrink-0 text-center ${i === 0 ? 'bg-primary-500 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300'}`}>
                      <span className="text-[8px] font-bold leading-none">{dayLabel}</span>
                      <span className="text-[10px] font-bold leading-none mt-0.5">{dt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs font-semibold text-slate-800 dark:text-white truncate">{camp.name}</div>
                      <div className="text-[10px] text-slate-500 dark:text-slate-400 truncate">{camp.listName}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col flex-1 items-center justify-center text-center py-4">
              <Calendar size={28} className="text-slate-300 dark:text-slate-600 mb-2" />
              <div className="text-slate-400 text-sm">No scheduled campaigns</div>
              <div className="text-xs text-slate-400 mt-1">Schedule a campaign to see it here</div>
            </div>
          )}
        </div>
      </div>

      {/* Recent Campaign Performance */}
      <div className="glass-panel rounded-2xl p-6 overflow-hidden">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-sm font-bold text-slate-800 dark:text-white uppercase tracking-wider flex items-center gap-2">
            <FileSpreadsheet size={16} className="text-primary-500" />
            Recent Campaign Performance
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-slate-200 dark:border-white/5 text-slate-500 dark:text-slate-400 text-xs font-semibold uppercase tracking-wider">
                <th className="py-3.5 px-4">Campaign</th>
                <th className="py-3.5 px-4">List</th>
                <th className="py-3.5 px-4">Status</th>
                <th className="py-3.5 px-4">Sent</th>
                <th className="py-3.5 px-4">Open %</th>
                <th className="py-3.5 px-4">Click %</th>
                <th className="py-3.5 px-4">Bounce %</th>
                <th className="py-3.5 px-4">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-white/5 text-sm text-slate-600 dark:text-slate-300">
              {recentCampaigns.map((camp: any) => {
                const openRate = camp.stats?.openRate ?? 0;
                const clickRate = camp.stats?.clickRate ?? 0;
                const bounceRate = camp.stats?.bounceRate ?? 0;
                return (
                  <tr key={camp.id} className="hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
                    <td className="py-3.5 px-4">
                      <div className="font-medium text-slate-800 dark:text-white">{camp.name}</div>
                      <div className="text-[11px] text-slate-400 truncate max-w-[180px]">{camp.subject}</div>
                    </td>
                    <td className="py-3.5 px-4 text-xs">{camp.listName}</td>
                    <td className="py-3.5 px-4">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${BADGE_COLOR[camp.status] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                        {camp.status === 'PAUSED_BOUNCE_LIMIT' ? 'BOUNCE LIMIT' : camp.status}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 font-semibold text-slate-700 dark:text-slate-200 tabular-nums">
                      {(camp.stats?.sent || 0).toLocaleString()}
                    </td>
                    <td className="py-3.5 px-4">
                      <span className="px-1.5 py-0.5 rounded text-[11px] font-semibold bg-primary-500/10 text-primary-600 dark:text-primary-500 tabular-nums">
                        {openRate}%
                      </span>
                    </td>
                    <td className="py-3.5 px-4">
                      <span className="px-1.5 py-0.5 rounded text-[11px] font-semibold bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 tabular-nums">
                        {clickRate}%
                      </span>
                    </td>
                    <td className="py-3.5 px-4">
                      <span className={`px-1.5 py-0.5 rounded text-[11px] font-semibold tabular-nums ${bounceRate >= 9 ? 'bg-rose-500/20 text-rose-600 dark:text-rose-400 font-bold' : bounceRate >= 5 ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400' : 'bg-slate-100 dark:bg-slate-800 text-slate-500'}`}>
                        {bounceRate}%
                      </span>
                    </td>
                    <td className="py-3.5 px-4 text-xs text-slate-400 dark:text-slate-500">
                      {new Date(camp.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                );
              })}
              {recentCampaigns.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center py-8 text-slate-500">
                    No campaigns created yet. Go to Campaigns to create one!
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
export default Dashboard;
